import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { skipToken } from "@reduxjs/toolkit/query";

import { selectCurrentUser } from "@/redux/authentication/selectors";
import { useGetContributionGradesQuery } from "@/redux/service";
import { isPaginatedResponse } from "@/redux/typeGuards";
import BaseTabs from "@/components_v2/Tabs/BaseTabs";
import SwitchPathModal from "@/domains/certifications/components/SwitchPathModal";
import {
  needsEdit,
  withContributionStatus,
} from "@/domains/certifications/contributions";
import { useCommunityActivity } from "@/domains/certifications/hooks/useCommunityActivity";
import { useKnowledgeCheck } from "@/domains/certifications/hooks/useKnowledgeCheck";
import {
  findKnowledgeCheckId,
  getQuestionCount,
} from "@/domains/certifications/knowledgeCheck";
import {
  getContentKey,
  getPathCopy,
  getUpcomingLiveSessions,
} from "@/domains/certifications/utils";
import CommunityCard from "@/features/certifications/module/components/CommunityCard";
import HelpCard from "@/features/certifications/module/components/HelpCard";
import LiveSessionsStrip from "@/features/certifications/module/components/LiveSessionsStrip";
import LockedRequirementCard from "@/features/certifications/module/components/LockedRequirementCard";
import KnowledgeCheckCard from "@/features/certifications/module/components/KnowledgeCheckCard";
import ModuleHeader from "@/features/certifications/module/components/ModuleHeader";
import ReadingsCard from "@/features/certifications/module/components/ReadingsCard";
import ResourcesCard from "@/features/certifications/module/components/ResourcesCard";
import VideosCard from "@/features/certifications/module/components/VideosCard";
import WeekProgressPanel from "@/features/certifications/module/components/WeekProgressPanel";
import {
  CertificationModuleProps,
  ModuleTab,
  ModuleTabKey,
} from "@/features/certifications/module/types";
import { MODULE_TABS } from "@/features/certifications/module/constants";
import {
  CONTENT_ITEM_TYPES,
  CONTRIBUTION_KINDS,
} from "@/domains/certifications/constants";

import "@/domains/certifications/components/styles.css";
import "./styles.css";
import { certificationRouteKey } from "@/domains/certifications/routes";

const CertificationModule = ({
  certification,
  track,
  moduleWeek,
  moduleProgress,
}: CertificationModuleProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ModuleTabKey>(
    MODULE_TABS.THIS_WEEK,
  );
  const [isSwitchOpen, setIsSwitchOpen] = useState(false);

  const contentKey = getContentKey(certification.type);
  const pathCopy = getPathCopy(t, contentKey, track);
  const { week } = moduleWeek;
  const videos = week.module.items
    .filter((item) => item.type === CONTENT_ITEM_TYPES.VIDEO)
    .sort((a, b) => a.order - b.order);
  const readings = week.module.items
    .filter(
      (item) =>
        item.type === CONTENT_ITEM_TYPES.RESOURCE &&
        item.id in week.itemSatisfaction,
    )
    .sort((a, b) => a.order - b.order);
  const resourceItems = week.module.items.filter(
    (item) => !readings.includes(item),
  );
  const knowledgeCheckId = findKnowledgeCheckId(week.module, moduleProgress);
  const {
    data: knowledgeCheck,
    state: knowledgeCheckState,
    isError: isKnowledgeCheckError,
  } = useKnowledgeCheck({
    certificationSlug: certification.slug,
    knowledgeCheckId,
  });
  const otherTrack =
    certification.tracks.find((item) => item.id !== track.id) ?? null;
  const isLocked = certification.enrollment?.isLocked ?? false;
  const switchLabel =
    otherTrack && !isLocked
      ? t("certification.module.locked.switch", {
          path: getPathCopy(t, contentKey, otherTrack).shortTitle,
        })
      : null;
  const hasCheckItem = week.module.items.some(
    (item) => item.type === CONTENT_ITEM_TYPES.KNOWLEDGE_CHECK,
  );
  const user = useSelector(selectCurrentUser);
  const { topic } = week.module;
  const isCommunityRequired = Boolean(week.posts || week.comments);
  const activity = useCommunityActivity({
    topicIds: topic && isCommunityRequired ? [topic.id] : null,
    userId: user?.id ?? null,
    includeRemoved: true,
  });
  const grades = useGetContributionGradesQuery(
    isCommunityRequired
      ? { module: week.module.id, pagination: "off" }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  );
  const contributions = useMemo(
    () =>
      withContributionStatus(
        activity.items,
        grades.data
          ? isPaginatedResponse(grades.data)
            ? grades.data.results
            : grades.data
          : [],
      ),
    [activity.items, grades.data],
  );
  const needingEdit = contributions.filter(({ status }) => needsEdit(status));
  const editNeeded = needingEdit.some(
    ({ kind }) => kind === CONTRIBUTION_KINDS.POST,
  )
    ? CONTRIBUTION_KINDS.POST
    : (needingEdit[0]?.kind ?? null);
  const tabs = [
    {
      key: MODULE_TABS.THIS_WEEK,
      label: t("certification.module.tabs.thisWeek"),
    },
    {
      key: MODULE_TABS.RESOURCES,
      label: t("certification.module.tabs.resources"),
    },
  ] satisfies ModuleTab[];

  return (
    <div className="flex flex-col gap-500">
      <ModuleHeader
        certification={certification}
        week={week}
        pathTitle={pathCopy.title}
        questionCount={knowledgeCheck ? getQuestionCount(knowledgeCheck) : null}
      />

      <LiveSessionsStrip sessions={getUpcomingLiveSessions(certification)} />

      <div className="flex flex-col gap-400">
        <BaseTabs
          items={tabs}
          urlSync={false}
          defaultActiveKey={activeTab}
          onChange={(key) => setActiveTab(key as ModuleTabKey)}
        />

        <div className="module-layout flex gap-600">
          <div className="module-main">
            {activeTab === MODULE_TABS.THIS_WEEK && (
              <div className="flex flex-col gap-400">
                {videos.length > 0 && (
                  <VideosCard
                    certificationSlug={certification.slug}
                    weekPosition={week.position}
                    videos={videos}
                    itemProgress={moduleProgress.items}
                    isRequired={week.videos !== null}
                    itemSatisfaction={week.itemSatisfaction}
                  />
                )}

                {readings.length > 0 && (
                  <ReadingsCard
                    moduleId={week.module.id}
                    certificationSlug={certification.slug}
                    items={readings}
                    itemSatisfaction={week.itemSatisfaction}
                  />
                )}

                {week.knowledgeCheck && knowledgeCheckId !== null && (
                  <KnowledgeCheckCard
                    certificationSlug={certification.slug}
                    isLocked={isLocked}
                    weekPosition={week.position}
                    check={knowledgeCheck}
                    state={knowledgeCheckState}
                    isError={isKnowledgeCheckError}
                  />
                )}

                {hasCheckItem && !week.knowledgeCheck && (
                  <LockedRequirementCard
                    title={t("certification.module.check.heading")}
                    body={t("certification.module.locked.checkBody")}
                    switchLabel={switchLabel}
                    onSwitch={() => setIsSwitchOpen(true)}
                  />
                )}

                {topic && isCommunityRequired && (
                  <CommunityCard
                    topic={topic}
                    posts={week.posts}
                    comments={week.comments}
                    user={user}
                    activity={contributions}
                    isActivityLoading={activity.isLoading || grades.isLoading}
                    isActivityError={activity.isError}
                  />
                )}

                {topic && !isCommunityRequired && (
                  <LockedRequirementCard
                    title={t("certification.module.community.heading")}
                    body={t("certification.module.locked.communityBody")}
                    switchLabel={switchLabel}
                    onSwitch={() => setIsSwitchOpen(true)}
                  />
                )}
              </div>
            )}

            {activeTab === MODULE_TABS.RESOURCES && (
              <ResourcesCard
                moduleId={week.module.id}
                certificationSlug={certification.slug}
                items={resourceItems}
                itemProgress={moduleProgress.items}
                allItems={certification.modules.flatMap(
                  (module) => module.items,
                )}
              />
            )}
          </div>

          <aside className="module-rail flex flex-col gap-400">
            <WeekProgressPanel
              slug={certificationRouteKey(certification)}
              week={week}
              nextWeek={moduleWeek.nextWeek}
              pathTitle={pathCopy.title}
              editNeeded={editNeeded}
            />

            <HelpCard
              question={pathCopy.helpQuestion}
              linkLabel={pathCopy.helpLink}
            />
          </aside>
        </div>
      </div>

      <SwitchPathModal
        open={isSwitchOpen}
        certification={certification}
        targetTrack={otherTrack}
        onClose={() => setIsSwitchOpen(false)}
        onSwitched={() => setIsSwitchOpen(false)}
      />
    </div>
  );
};

export default CertificationModule;
